import type { CSSProperties } from 'react';
import type { Entity } from '@backstage/catalog-model';

/**
 * Dependency grading as authored in topology*.json and ingested by the
 * system-registry provider: a dependency is either `critical` (losing it takes
 * the source down) or `noncritical`, in which case `severity` qualifies how
 * badly the source degrades.
 */
export type DependencyCriticality = 'critical' | 'noncritical' | 'unknown';

export interface DependencyGrade {
  criticality: DependencyCriticality;
  /** Only meaningful for non-critical dependencies. Empty when unspecified. */
  severity: string;
}

const CRITICALITY_ANNOTATION = 'verint.com/dependency-criticality';
const SEVERITY_ANNOTATION = 'verint.com/dependency-severities';

function parseAnnotation(raw: string | undefined): Record<string, string> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Reads the per-target dependency grades written by the system-registry provider. */
export function readDependencyGrades(entity: Entity): Record<string, DependencyGrade> {
  const annotations = (entity.metadata.annotations ?? {}) as Record<string, string>;
  const criticalities = parseAnnotation(annotations[CRITICALITY_ANNOTATION]);
  const severities = parseAnnotation(annotations[SEVERITY_ANNOTATION]);

  const grades: Record<string, DependencyGrade> = {};
  for (const ref of new Set([...Object.keys(criticalities), ...Object.keys(severities)])) {
    grades[ref] = toGrade(criticalities[ref], severities[ref]);
  }
  return grades;
}

/**
 * Normalises a raw criticality/severity pair. Feeds predating the `type` field
 * encoded criticality in `severity`, so `severity: critical` still maps to a
 * critical dependency and a bare `unknown` severity stays unknown rather than
 * being downgraded to non-critical.
 */
export function toGrade(criticality?: string, severity?: string): DependencyGrade {
  const c = (criticality ?? '').toLowerCase().replace(/[^a-z]/g, '');
  const s = (severity ?? '').toLowerCase();
  if (c === 'critical' || (!c && s === 'critical')) {
    return { criticality: 'critical', severity: '' };
  }
  if (c === 'noncritical') {
    return { criticality: 'noncritical', severity: s === 'unknown' ? '' : s };
  }
  if (!s || s === 'unknown') {
    return { criticality: 'unknown', severity: '' };
  }
  return { criticality: 'noncritical', severity: s };
}

/** e.g. `Critical`, `Non-critical (medium)`, `Non-critical`, `Unknown`. */
export function dependencyLabel(grade: DependencyGrade): string {
  if (grade.criticality === 'critical') return 'Critical';
  if (grade.criticality === 'unknown') return 'Unknown';
  return grade.severity ? `Non-critical (${grade.severity})` : 'Non-critical';
}

/** Ordering bucket used to pick colours and line weights. */
export function dependencyTier(
  grade: DependencyGrade,
): 'critical' | 'high' | 'medium' | 'low' | 'unknown' {
  if (grade.criticality === 'critical') return 'critical';
  if (grade.criticality === 'unknown') return 'unknown';
  if (grade.severity === 'high') return 'high';
  if (grade.severity === 'medium') return 'medium';
  return 'low';
}

/**
 * Colours are weighted white→transparent-gray in dark mode ("critical reads as
 * weight rather than hue, leaving red free to mean 'down'") but that scale is
 * invisible on a light background, so light mode gets a mirrored dark→gray scale.
 */
const DEPENDENCY_COLORS_DARK: Record<string, string> = {
  critical: 'rgba(255,255,255,0.9)',
  high: 'rgba(255,112,67,0.8)',
  medium: 'rgba(255,152,0,0.6)',
  low: 'rgba(255,193,7,0.45)',
  unknown: 'rgba(139,148,158,0.5)',
};

const DEPENDENCY_COLORS_LIGHT: Record<string, string> = {
  critical: 'rgba(66,66,66,0.85)',
  high: 'rgba(191,54,12,0.85)',
  medium: 'rgba(230,81,0,0.75)',
  low: 'rgba(255,143,0,0.65)',
  unknown: 'rgba(84,110,122,0.7)',
};

export function getDependencyColors(isDark: boolean): Record<string, string> {
  return isDark ? DEPENDENCY_COLORS_DARK : DEPENDENCY_COLORS_LIGHT;
}

export function dependencyChipStyle(grade: DependencyGrade): CSSProperties {
  switch (dependencyTier(grade)) {
    case 'critical':
      return { background: 'rgba(244,67,54,0.32)', color: '#ff5252' };
    case 'high':
      return { background: 'rgba(255,112,67,0.2)', color: '#ff7043' };
    case 'medium':
      return { background: 'rgba(255,152,0,0.18)', color: '#ffa726' };
    case 'unknown':
      return { background: 'rgba(255,255,255,0.08)', color: '#8b949e' };
    default:
      return { background: 'rgba(255,193,7,0.16)', color: '#ffc107' };
  }
}
